/*
 * Provides reactive online/offline status tracking for the application.
 *
 * This composable function tracks the user's online status and displays a temporary
 * message when the user comes back online. It uses Vue's `ref` for reactivity and
 * lifecycle hooks to manage event listeners for the `online` and `offline` events.
 *
 * @returns {Object} - An object containing:
 *   - `isOnline` {Ref<boolean>}: A reactive reference indicating the current online status.
 *   - `showOnlineMessage` {Ref<boolean>}: A reactive reference indicating whether to show the online message.
 */
export function useOnlineStatus () {
  /*
     * Always init true so SSR markup matches first client render; real value
     * read inside onMounted to avoid hydration mismatch.
     */
  const isOnline = ref(true)

  /*
     * Reactive reference to control the visibility of the online status message.
     * This message is shown temporarily when the user comes back online.
     */
  const showOnlineMessage = ref(false)

  /*
     * Updates the online status and shows a temporary message when the user comes back online.
     */
  const updateStatus = () => {
    if (typeof navigator !== 'undefined') {
      if (navigator.onLine) {
        showOnlineMessage.value = true
        setTimeout(() => {
          showOnlineMessage.value = false
        }, 3000) // Keep it visible for 3 seconds before fading
      }
      isOnline.value = navigator.onLine
    }
  }

  /*
     * Sets up event listeners for `online` and `offline` events when the component is mounted.
     * Only runs in browser context.
     */
  onMounted(() => {
    if (typeof window !== 'undefined') {
      isOnline.value = navigator.onLine
      window.addEventListener('online', updateStatus)
      window.addEventListener('offline', updateStatus)
    }
  })

  /*
     * Removes event listeners for `online` and `offline` events when the component is unmounted.
     * Only runs in browser context.
     */
  onBeforeUnmount(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', updateStatus)
      window.removeEventListener('offline', updateStatus)
    }
  })

  // Return the reactive references for use in components
  return { isOnline, showOnlineMessage }
}